import React, { useState } from 'react';
import { PlusIcon, Edit2Icon, Trash2Icon, TargetIcon } from 'lucide-react';
import type { Goal } from '../../types';
import { Header } from '../layout/Header';
import { GoalForm } from './GoalForm';
import { getGoals } from '../../data/mockData';
import { formatMetricLabel } from '../../utils/calculations';
function formatAssignment(goal: Goal): string {
  if (goal.assignmentLevel === 'company') return 'Company-Wide';
  const labels: Record<string, string> = {
    division: 'Division',
    team: 'Team',
    loanOfficer: 'Loan Officer'
  };
  return `${labels[goal.assignmentLevel]}: ${goal.assignmentId ?? '—'}`;
}
export function GoalConfigPage() {
  const [goals, setGoals] = useState<Goal[]>(getGoals());
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | undefined>(undefined);
  const handleSave = (goal: Goal) => {
    setGoals(prev => {
      const idx = prev.findIndex(g => g.id === goal.id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = goal;
        return updated;
      }
      return [...prev, goal];
    });
    setShowForm(false);
    setEditingGoal(undefined);
  };
  const handleEdit = (goal: Goal) => {
    setEditingGoal(goal);
    setShowForm(true);
  };
  const handleDelete = (goalId: string) => {
    setGoals(prev => prev.filter(g => g.id !== goalId));
  };
  const handleCancel = () => {
    setShowForm(false);
    setEditingGoal(undefined);
  };
  return <div className="min-h-screen" data-id="element-126">
      <Header title="Configure Goals" subtitle="Set performance targets for Soft Pull and Submitted metrics" data-id="element-127">
        {!showForm && <button onClick={() => {
        setEditingGoal(undefined);
        setShowForm(true);
      }} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm" aria-label="Add new goal" data-id="element-128">
            <PlusIcon className="w-4 h-4" data-id="element-129" />
            Add Goal
          </button>}
      </Header>

      <div className="px-6 lg:px-8 py-6 space-y-6" data-id="element-130">
        {/* Form */}
        {showForm && <GoalForm goal={editingGoal} onSave={handleSave} onCancel={handleCancel} data-id="element-131" />}

        {/* Goals Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors" data-id="element-132">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5" data-id="element-133">
            <TargetIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" data-id="element-134" />
            <h3 className="text-base font-semibold text-gray-900 dark:text-white" data-id="element-135">
              Current Goals
            </h3>
            <span className="text-sm text-gray-400 dark:text-gray-500 ml-1" data-id="element-136">
              ({goals.length})
            </span>
          </div>

          {goals.length === 0 ? <div className="px-6 py-12 text-center" data-id="element-137">
              <TargetIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-138" />
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-139">
                No goals configured yet. Click "Add Goal" to get started.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-140">
              <table className="w-full text-sm" aria-label="Goals configuration table" data-id="element-141">
                <thead data-id="element-142">
                  <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700" data-id="element-143">
                    <th className="text-left px-6 py-3.5 font-semibold text-gray-600 dark:text-gray-300 uppercase text-xs tracking-wider" data-id="element-144">
                      Metric
                    </th>
                    <th className="text-left px-6 py-3.5 font-semibold text-gray-600 dark:text-gray-300 uppercase text-xs tracking-wider" data-id="element-145">
                      Timeframe
                    </th>
                    <th className="text-right px-6 py-3.5 font-semibold text-gray-600 dark:text-gray-300 uppercase text-xs tracking-wider" data-id="element-146">
                      Target
                    </th>
                    <th className="text-left px-6 py-3.5 font-semibold text-gray-600 dark:text-gray-300 uppercase text-xs tracking-wider" data-id="element-147">
                      Assignment
                    </th>
                    <th className="text-left px-6 py-3.5 font-semibold text-gray-600 dark:text-gray-300 uppercase text-xs tracking-wider" data-id="element-148">
                      Effective
                    </th>
                    <th className="text-right px-6 py-3.5 font-semibold text-gray-600 dark:text-gray-300 uppercase text-xs tracking-wider" data-id="element-149">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700" data-id="element-150">
                  {goals.map(goal => <tr key={goal.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors" data-id="element-151">
                      <td className="px-6 py-4 font-semibold text-gray-900 dark:text-white" data-id="element-152">
                        {formatMetricLabel(goal.metric)}
                      </td>
                      <td className="px-6 py-4 text-gray-600 dark:text-gray-300 capitalize" data-id="element-153">
                        {goal.timeframe}
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-gray-900 dark:text-white" data-id="element-154">
                        {goal.target}
                      </td>
                      <td className="px-6 py-4 text-gray-600 dark:text-gray-300" data-id="element-155">
                        {formatAssignment(goal)}
                      </td>
                      <td className="px-6 py-4 text-gray-500 dark:text-gray-400" data-id="element-156">
                        {goal.effectiveDate}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-157">
                        <div className="inline-flex items-center gap-1" data-id="element-158">
                          <button onClick={() => handleEdit(goal)} className="p-2 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md transition-colors" aria-label={`Edit ${formatMetricLabel(goal.metric)} ${goal.timeframe} goal`} data-id="element-159">
                            <Edit2Icon className="w-4 h-4" data-id="element-160" />
                          </button>
                          <button onClick={() => handleDelete(goal.id)} className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors" aria-label={`Delete ${formatMetricLabel(goal.metric)} ${goal.timeframe} goal`} data-id="element-161">
                            <Trash2Icon className="w-4 h-4" data-id="element-162" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    </div>;
}